import { useState, useEffect, useRef, useCallback } from "react";
import FadeIn from "./FadeIn";

const SLIDER_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/k03sh5nl_9.png",
  "https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/kvu0ehxi_1.png",
  "https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/s04etc31_2.png",
  "https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/j46bqg7q_3.png",
  "https://customer-assets.emergentagent.com/job_7135488c-395e-477c-9ceb-46a81f357cdf/artifacts/rul1j8wg_4.png",
  "https://customer-assets.emergentagent.com/job_a88f344e-8f3e-4749-8647-43dae95be213/artifacts/uto856q5_5.png",
  "https://customer-assets.emergentagent.com/job_a88f344e-8f3e-4749-8647-43dae95be213/artifacts/m7plfd3k_6.png",
  "https://customer-assets.emergentagent.com/job_a88f344e-8f3e-4749-8647-43dae95be213/artifacts/xemyahi1_7.png",
  "https://customer-assets.emergentagent.com/job_a88f344e-8f3e-4749-8647-43dae95be213/artifacts/ocx5rxif_8.png",
];

export default function HowPeopleUseHaevn() {
  const count = SLIDER_PHOTOS.length;
  const extended = [...SLIDER_PHOTOS, ...SLIDER_PHOTOS, ...SLIDER_PHOTOS];
  const [pos, setPos] = useState(count);
  const [animate, setAnimate] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const autoPlayRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const slideW = isMobile ? 88 : 30;
  const gap = isMobile ? 3 : 1.5;
  const step = slideW + gap;
  const centerOffset = (100 - slideW) / 2;

  const resetAutoPlay = useCallback(() => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setPos((p) => p + 1);
    }, 8000);
  }, []);

  useEffect(() => {
    resetAutoPlay();
    return () => clearInterval(autoPlayRef.current);
  }, [resetAutoPlay]);

  useEffect(() => {
    if (!animate) return;
    if (pos >= count * 2) {
      const t = setTimeout(() => {
        setAnimate(false);
        setPos((p) => p - count);
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      }, 520);
      return () => clearTimeout(t);
    }
    if (pos < count) {
      const t = setTimeout(() => {
        setAnimate(false);
        setPos((p) => p + count);
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      }, 520);
      return () => clearTimeout(t);
    }
  }, [pos, count, animate]);

  const goTo = (realIdx) => {
    const currentReal = ((pos % count) + count) % count;
    let diff = realIdx - currentReal;
    if (diff > count / 2) diff -= count;
    if (diff < -count / 2) diff += count;
    setPos((p) => p + diff);
    resetAutoPlay();
  };

  const handlePointerDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX || e.touches?.[0]?.clientX || 0);
    setDragOffset(0);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const x = e.clientX || e.touches?.[0]?.clientX || 0;
    setDragOffset(x - startX);
  };

  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (Math.abs(dragOffset) > 50) {
      const dir = dragOffset > 0 ? -1 : 1;
      setPos((p) => p + dir);
      resetAutoPlay();
    }
    setDragOffset(0);
  };

  const dragPercent = trackRef.current
    ? (dragOffset / trackRef.current.offsetWidth) * 100
    : 0;
  const tx = centerOffset - pos * step + dragPercent;
  const realIndex = ((pos % count) + count) % count;

  return (
    <section className="pt-12 md:pt-16 px-6 md:px-12 bg-white overflow-hidden" data-testid="how-people-use-section">
      <div className="max-w-5xl mx-auto mb-12">
        <FadeIn>
          <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">How People Use HAEVN</p>
          <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy leading-tight">Different people. Different intentions. Same system.</h2>
        </FadeIn>
      </div>
      <div className="relative -mx-6 md:-mx-12">
        <div
          ref={trackRef}
          className="relative select-none cursor-grab active:cursor-grabbing"
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          data-testid="photo-slider-track"
        >
          <div
            className={`flex ${isDragging ? "" : animate ? "transition-transform duration-500 ease-out" : ""}`}
            style={{ transform: `translateX(${tx}%)`, gap: `${gap}%` }}
          >
            {extended.map((src, i) => {
              const isActive = i === pos;
              return (
                <div
                  key={i}
                  className="flex-shrink-0 transition-all duration-500"
                  style={{ width: `${slideW}%` }}
                >
                  <div className={`rounded overflow-hidden transition-opacity duration-500 ${isActive ? "opacity-100" : "opacity-60"}`}>
                    <img
                      src={src}
                      alt={`HAEVN lifestyle ${(i % count) + 1}`}
                      className="w-full h-auto"
                      draggable={false}
                      data-testid={isActive ? `slider-photo-active` : undefined}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-[calc((100%-64rem)/2)] md:w-[calc((100%-64rem)/2+0.5rem)] bg-gradient-to-r from-white to-transparent hidden md:block" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-[calc((100%-64rem)/2)] md:w-[calc((100%-64rem)/2+0.5rem)] bg-gradient-to-l from-white to-transparent hidden md:block" />
      </div>
      <div className="flex justify-center gap-2.5 mt-8">
        {SLIDER_PHOTOS.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-colors duration-200 ${i === realIndex ? "bg-haevn-navy" : "bg-gray-300"}`}
            aria-label={`Photo ${i + 1}`}
            data-testid={`slider-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}
